import {
  useState,
  useCallback,
  useRef,
  useEffect,
  MutableRefObject,
} from "react";
import { ThreeEvent } from "@react-three/fiber";
import { Vector2, Mesh } from "three";
import { StickerConfig } from "@/lib/sticker";
import { calculateStickerSizeTexture } from "@/lib/sizeCalculator";
import { ModelConfig } from "@/lib/models";
import { TEXTURE_SIZE } from "@/lib/constants";

/**
 * 鼠标拖拽状态管理接口
 *
 * 状态转换流程:
 * [default] ←→ [grab] → [grabbing]
 *     ↑         ↓         ↓
 *     └─────────┴─────────┘
 *
 * - default: isDragging=false, isHovering=false (光标: default)
 * - grab: isDragging=false, isHovering=true (光标: grab)
 * - grabbing: isDragging=true, isHovering=true/false (光标: grabbing)
 */
interface DragState {
  /** 是否正在拖拽贴纸 */
  isDragging: boolean;
  /** 鼠标是否在贴纸区域内 */
  isHovering: boolean;
  /** 拖拽开始时鼠标与贴纸中心的偏移量，用于计算拖拽位置 */
  dragOffset: Vector2 | null;
}

/**
 * 贴纸纹理拖拽功能的 Hook 参数接口
 */
interface StickerTextureDragProps {
  /** 贴纸配置信息，包含图片、尺寸等 */
  stickerConfig: StickerConfig;
  /** 3D 模型配置信息，用于坐标计算 */
  modelConfig: ModelConfig | undefined;
  /** 当前贴纸在 UV 坐标系中的位置 */
  currentUvPoint: Vector2 | null;
  /** UV 坐标更新回调函数 */
  onUvPointUpdate: (uvPoint: Vector2) => void;
  /** 3D 模型 Mesh 引用 */
  meshRef: MutableRefObject<Mesh | null>;
}

/**
 * 贴纸纹理拖拽交互功能 Hook
 *
 * 提供完整的贴纸拖拽交互功能，包括：
 * - 鼠标状态检测和管理 (default/grab/grabbing)
 * - 智能光标样式切换
 * - 全局拖拽状态处理
 * - UV 坐标精确计算和更新
 *
 * @param props - 拖拽功能配置参数
 * @returns 拖拽交互相关的状态和处理函数
 *
 * @example
 * ```typescript
 * const textureDrag = useStickerTextureDrag({
 *   stickerConfig,
 *   modelConfig,
 *   currentUvPoint: textureUvPoint,
 *   onUvPointUpdate: onTextureUvUpdate,
 *   meshRef,
 * });
 *
 * // 在组件中使用
 * <Model
 *   onPointerMove={textureDrag.handleMouseMove}
 *   onPointerDown={textureDrag.handleMouseDown}
 *   cursorStyle={textureDrag.getCursorStyle()}
 * />
 * ```
 */
export const useStickerTextureDrag = ({
  stickerConfig,
  modelConfig,
  currentUvPoint,
  onUvPointUpdate,
  meshRef,
}: StickerTextureDragProps) => {
  // === 状态管理 ===

  /** 当前拖拽状态 */
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    isHovering: false,
    dragOffset: null,
  });

  /** 拖拽开始时的 UV 坐标位置引用 */
  const dragStartRef = useRef<Vector2 | null>(null);

  /** 最后记录的鼠标 UV 坐标位置，用于状态恢复 */
  const lastMousePositionRef = useRef<Vector2 | null>(null);

  /**
   * 状态更新工具函数 - 减少重复代码
   * React 保证 setState 函数是稳定的，不会在重新渲染时改变
   * @param updates - 要更新的状态字段
   */
  const updateDragState = useCallback((updates: Partial<DragState>) => {
    setDragState((prev) => ({ ...prev, ...updates }));
  }, []);

  // === 工具函数 ===

  /**
   * 检查指定 UV 坐标点是否在贴纸区域内
   *
   * 算法说明：
   * 1. 根据贴纸配置计算在纹理上的像素尺寸
   * 2. 转换为 UV 坐标尺寸（0-1 范围）
   * 3. 以当前贴纸中心为基准计算边界框
   * 4. 判断目标点是否在边界框内
   *
   * @param uv - 要检查的 UV 坐标点
   * @returns 是否在贴纸区域内
   */
  const isPointInSticker = useCallback(
    (uv: Vector2): boolean => {
      if (!currentUvPoint || !modelConfig) {
        return false;
      }

      // 计算贴纸在UV坐标系中的尺寸（归一化坐标）
      const stickerSizeTexture = calculateStickerSizeTexture(
        stickerConfig,
        modelConfig,
        TEXTURE_SIZE,
      );

      // 将像素尺寸转换为UV坐标尺寸（0-1范围）
      const uvWidth = stickerSizeTexture.width / TEXTURE_SIZE;
      const uvHeight = stickerSizeTexture.height / TEXTURE_SIZE;

      // 计算贴纸边界框
      const left = currentUvPoint.x - uvWidth / 2;
      const right = currentUvPoint.x + uvWidth / 2;
      const top = currentUvPoint.y + uvHeight / 2;
      const bottom = currentUvPoint.y - uvHeight / 2;

      // 检查点是否在边界内
      return uv.x >= left && uv.x <= right && uv.y >= bottom && uv.y <= top;
    },
    [currentUvPoint, stickerConfig, modelConfig],
  );

  // === 全局事件处理 ===

  /**
   * 全局鼠标释放事件监听器
   *
   * 处理边界情况：当用户在拖拽过程中将鼠标移出 3D 模型区域并松开鼠标时，
   * 3D 模型的 onPointerUp 事件不会被触发，因此需要全局监听器来确保
   * 拖拽状态能够正确结束。
   *
   * 状态转换: [grabbing] → [grab/default]
   * - 根据最后记录的鼠标位置判断最终状态
   * - 在贴纸区域内 → grab 状态
   * - 在贴纸区域外 → default 状态
   */
  useEffect(() => {
    if (!dragState.isDragging) return;

    const handleGlobalPointerUp = () => {
      if (dragState.isDragging) {
        // 根据最后的鼠标位置判断是否还在贴纸区域内
        let isHovering = false;
        if (
          lastMousePositionRef.current &&
          currentUvPoint &&
          stickerConfig.image
        ) {
          isHovering = isPointInSticker(lastMousePositionRef.current);
        }

        updateDragState({
          isDragging: false,
          dragOffset: null,
          isHovering, // 根据最后位置设置hover状态
        });
        dragStartRef.current = null;
      }
    };

    document.addEventListener("pointerup", handleGlobalPointerUp);

    return () => {
      document.removeEventListener("pointerup", handleGlobalPointerUp);
    };
  }, [
    dragState.isDragging,
    currentUvPoint,
    stickerConfig.image,
    isPointInSticker,
  ]);

  // === 事件处理器 ===

  /**
   * 处理鼠标移动事件
   *
   * 职责：
   * 1. hover 状态检测和管理 (default ↔ grab)
   * 2. 拖拽过程中的位置更新 (grabbing 状态下)
   * 3. 记录鼠标位置用于后续状态恢复
   *
   * 状态转换：
   * - 进入贴纸区域: [default] → [grab]
   * - 离开贴纸区域: [grab] → [default]
   * - 拖拽中移动: 保持 [grabbing]，但可能停止位置更新
   *
   * @param event - Three.js 鼠标事件，包含 UV 坐标信息
   * @returns 当前 hover 状态，用于立即更新光标样式
   */
  const handleMouseMove = useCallback(
    (event: ThreeEvent<PointerEvent>): { isHovering: boolean } | null => {
      // 如果没有UV坐标或贴纸图片，重置hover状态
      if (!event.uv || !stickerConfig.image) {
        lastMousePositionRef.current = null;
        updateDragState({ isHovering: false });
        return { isHovering: false };
      }

      // 记录最后的鼠标位置，用于全局事件处理
      lastMousePositionRef.current = event.uv.clone();

      const isInSticker = isPointInSticker(event.uv);

      // 拖拽状态下的位置更新逻辑
      if (dragState.isDragging && dragState.dragOffset) {
        if (isInSticker) {
          // 在贴纸区域内：继续更新贴纸位置
          const newUvPoint = new Vector2(
            event.uv.x - dragState.dragOffset.x,
            event.uv.y - dragState.dragOffset.y,
          );

          // 确保 UV 坐标在有效范围内 (0-1)
          newUvPoint.x = Math.max(0, Math.min(1, newUvPoint.x));
          newUvPoint.y = Math.max(0, Math.min(1, newUvPoint.y));

          onUvPointUpdate(newUvPoint);
        }
        // 移出贴纸区域时：不停止拖拽状态，只是停止位置更新
      }

      // 更新 hover 状态（但保持拖拽状态不变）
      updateDragState({ isHovering: isInSticker });

      // 返回新状态用于立即更新光标样式
      return { isHovering: isInSticker };
    },
    [
      dragState.isDragging,
      dragState.dragOffset,
      isPointInSticker,
      onUvPointUpdate,
      stickerConfig.image,
    ],
  );

  /**
   * 处理鼠标按下事件 - 开始拖拽
   *
   * 职责：
   * 1. 检测点击是否在贴纸区域内
   * 2. 计算拖拽偏移量（鼠标相对于贴纸中心的偏移）
   * 3. 初始化拖拽状态
   * 4. 阻止事件冒泡以避免触发相机控制
   *
   * 状态转换: [grab] → [grabbing]
   * - 仅在贴纸区域内点击才会开始拖拽
   * - 在区域外点击不会有任何响应
   *
   * @param event - Three.js 鼠标按下事件
   */
  const handleMouseDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!event.uv || !currentUvPoint || !stickerConfig.image) {
        return;
      }

      const isInSticker = isPointInSticker(event.uv);

      if (isInSticker) {
        // 防止事件冒泡，避免触发相机控制
        event.stopPropagation();

        // 计算拖拽偏移量：鼠标位置相对于贴纸中心的偏移
        // 这个偏移量在整个拖拽过程中保持不变，确保贴纸跟随鼠标移动时位置准确
        const offset = new Vector2(
          event.uv.x - currentUvPoint.x,
          event.uv.y - currentUvPoint.y,
        );

        // 进入拖拽状态
        updateDragState({
          isDragging: true,
          dragOffset: offset,
        });

        // 记录拖拽开始时的贴纸位置
        dragStartRef.current = currentUvPoint.clone();
      }
      // 在贴纸区域外点击时不做任何操作
    },
    [currentUvPoint, isPointInSticker, stickerConfig.image],
  );

  /**
   * 处理鼠标释放事件 - 结束拖拽
   *
   * 职责：
   * 1. 结束拖拽状态
   * 2. 根据最终鼠标位置确定 hover 状态
   * 3. 清理拖拽相关的临时数据
   * 4. 返回最新状态供上层组件立即更新光标样式
   *
   * 状态转换: [grabbing] → [grab/default]
   * - 在贴纸区域内释放: [grabbing] → [grab]
   * - 在贴纸区域外释放: [grabbing] → [default]
   *
   * @param lastMouseEvent - 最后记录的鼠标事件，用于准确判断最终位置
   * @returns 新的拖拽和hover状态，null表示无需更新
   */
  const handleMouseUp = useCallback(
    (
      lastMouseEvent?: ThreeEvent<PointerEvent>,
    ): { isDragging: boolean; isHovering: boolean } | null => {
      if (!dragState.isDragging) {
        return null; // 没有在拖拽，不需要更新
      }

      // 根据最后的鼠标事件精确判断最终 hover 状态
      let isHovering = dragState.isHovering; // 使用当前状态作为默认值
      if (lastMouseEvent && lastMouseEvent.uv && stickerConfig.image) {
        isHovering = isPointInSticker(lastMouseEvent.uv);
      }

      const newState = {
        isDragging: false,
        isHovering,
      };

      // 更新状态：结束拖拽，清理偏移量，设置最终hover状态
      updateDragState({
        isDragging: false,
        dragOffset: null,
        isHovering,
      });

      // 清理拖拽开始位置记录
      dragStartRef.current = null;
      return newState;
    },
    [
      dragState.isDragging,
      dragState.isHovering,
      isPointInSticker,
      stickerConfig.image,
    ],
  );

  /**
   * 处理鼠标离开 3D 模型区域事件
   *
   * 职责：
   * 1. 处理鼠标离开 3D 模型区域的状态转换
   * 2. 在拖拽过程中保持拖拽状态，仅更新 hover 状态
   * 3. 清除位置记录，为全局事件处理做准备
   *
   * 状态转换逻辑：
   * - 非拖拽状态: [grab] → [default]
   * - 拖拽状态: [grabbing] → [grabbing] (保持拖拽，仅清除hover)
   *
   * 设计要点：
   * 拖拽中离开模型区域时不能立即结束拖拽，因为用户可能在模型外区域释放鼠标，
   * 此时需要全局事件监听器来处理最终的状态转换。
   *
   * @returns 更新后的状态信息，用于上层组件同步光标样式
   */
  const handleMouseLeave = useCallback((): {
    isHovering: boolean;
    isDragging?: boolean;
  } => {
    const wasDragging = dragState.isDragging;

    // 离开模型时清除最后的鼠标位置记录
    // 这样全局事件处理器就知道鼠标不在模型区域内
    lastMousePositionRef.current = null;

    if (wasDragging) {
      // 拖拽中离开：保持拖拽状态，仅清除 hover 状态
      updateDragState({ isHovering: false });
      // 保持 isDragging: true 和 dragOffset，直到鼠标释放

      return {
        isHovering: false,
        isDragging: true, // 明确表示继续拖拽状态
      };
    } else {
      // 非拖拽状态离开：正常清除 hover 状态
      updateDragState({ isHovering: false });

      return { isHovering: false };
    }
  }, [dragState.isDragging]);

  // === 样式计算 ===

  /**
   * 获取当前应显示的鼠标光标样式
   *
   * 光标样式规则：
   * - grabbing: isDragging = true (正在拖拽)
   * - grab: isDragging = false && isHovering = true (悬停在贴纸上)
   * - default: isDragging = false && isHovering = false (默认状态)
   *
   * @param overrideState - 可选的状态覆盖，用于在状态更新前预览样式
   * @returns CSS cursor 样式值
   */
  const getCursorStyle = useCallback(
    (overrideState?: Partial<DragState>): string => {
      const currentState = overrideState
        ? { ...dragState, ...overrideState }
        : dragState;

      if (currentState.isDragging) return "grabbing";
      if (currentState.isHovering) return "grab";
      return "default";
    },
    [dragState],
  );

  // === 返回值 ===

  return {
    /** 当前拖拽状态 */
    dragState,
    /** 鼠标移动事件处理器 */
    handleMouseMove,
    /** 鼠标按下事件处理器 */
    handleMouseDown,
    /** 鼠标释放事件处理器 */
    handleMouseUp,
    /** 鼠标离开事件处理器 */
    handleMouseLeave,
    /** 获取当前光标样式 */
    getCursorStyle,
    /** 检查点是否在贴纸区域内的工具函数 */
    isPointInSticker,
  };
};
